/**
 * NeedsPartnerBanner
 *
 * Top-of-dashboard warning surfaced when the viewer has team-roping
 * entries whose previous partner declined (`partner_status='needs_partner'`).
 * Without this banner the user had no high-level signal that an entry had
 * collapsed into payment-hold limbo — the inline per-card warning lives
 * deep inside the upcoming-rodeos list and was easy to scroll past.
 *
 * Render rules:
 *   - Hidden when there are no needs_partner registrations.
 *   - Lists each affected entry with two CTAs:
 *       Find Partner → opens FindNewPartnerDialog (same flow the inline
 *         card uses) so the user can re-pair without burning the original
 *         transaction fee.
 *       Cancel entry → opens CancelRegistrationDialog (same flow the
 *         inline card uses) so the user can back out and recover any
 *         refundable portion.
 *   - Capped at VISIBLE_LIMIT rows inline with a "+N more" hint.
 *
 * Data path: reuses /dashboard-data/my/upcoming-events. The backend's
 * status filter was widened to include 'pending_confirmation_and_payment'
 * specifically so partner-pending entries surface here.
 */

import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useMyUpcomingEvents } from '@/api/extensions/useDashboardData';
import {
  useCancelTeamRegistration,
} from '@/api/extensions/useRegistrationOps';
import { FindNewPartnerDialog } from '@/components/events/FindNewPartnerDialog';
import { CancelRegistrationDialog } from '@/components/events/CancelRegistrationDialog';
import { useToast } from '@/contexts/ToastContext';
import { formatCurrency } from '@/utils/formatters';

const VISIBLE_LIMIT = 5;

type NeedsPartnerRow = {
  registration_id: string;
  event_name: string;
  rodeo_name: string | null;
  position: string | null;
  event_id: string | null;
  paid_total: number;
};

export function NeedsPartnerBanner() {
  const { data, isLoading } = useMyUpcomingEvents();
  const { showSuccess, showError } = useToast();
  const cancelRegistration = useCancelTeamRegistration();

  const [findPartner, setFindPartner] = useState<{
    registrationId: string;
    eventId: string | null;
    primaryPosition: string | null;
    eventName: string;
  } | null>(null);
  const [pendingCancel, setPendingCancel] = useState<{
    id: string;
    description: string;
  } | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  if (isLoading) return null;
  const events = data ?? [];

  // Only team-roping primaries whose partner declined surface here. The
  // 'pending' (invitation outstanding) case is covered by the inline
  // amber banner on each card and by the existing PendingInvitationsCard
  // for the invitee — we don't duplicate that signal at the top.
  const needsPartner: NeedsPartnerRow[] = events
    .filter(
      (e: any) =>
        e.registration_type === 'team_primary' &&
        e.partner_status === 'needs_partner',
    )
    .map((e: any) => ({
      registration_id: e.registration_id,
      event_name:
        (typeof e.event?.event_name === 'string' && e.event.event_name.trim()) ||
        'Team Roping',
      rodeo_name:
        (typeof e.rodeo?.name === 'string' && e.rodeo.name.trim()) || null,
      position: e.position ?? null,
      event_id: e.event?.id ?? null,
      paid_total: parseFloat(String(e.payment_amount ?? 0)) || 0,
    }));

  if (needsPartner.length === 0) return null;

  const visible = needsPartner.slice(0, VISIBLE_LIMIT);
  const overflow = needsPartner.length - visible.length;

  const handleCancelConfirm = async () => {
    if (!pendingCancel) return;
    const id = pendingCancel.id;
    setCancellingId(id);
    try {
      const result = await cancelRegistration.mutateAsync(id);
      const successfulRefunds = (result?.refunds ?? []).filter(
        (r: any) => r.refunded,
      );
      const totalRefunded = successfulRefunds.reduce(
        (sum: number, r: any) => sum + (r.amount_refunded ?? 0),
        0,
      );
      if (totalRefunded > 0) {
        showSuccess(
          'Entry cancelled',
          `Refunded ${formatCurrency(totalRefunded)} to your original payment method.`,
        );
      } else {
        showSuccess('Entry cancelled');
      }
      setPendingCancel(null);
    } catch {
      showError('Failed to cancel entry');
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <>
      <Card
        className="border-destructive/60 bg-destructive/10 dark:border-red-900/60 dark:bg-red-950/30"
        role="region"
        aria-label="Team-roping entries that need a new partner"
      >
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-destructive/20 dark:bg-red-900/50 p-2 motion-safe:animate-pulse">
              <AlertTriangle
                className="h-5 w-5 text-destructive dark:text-red-300"
                aria-hidden="true"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-destructive dark:text-red-100">
                {needsPartner.length === 1
                  ? 'Your team-roping entry needs a new partner'
                  : `${needsPartner.length} team-roping entries need a new partner`}
              </h3>
              <p className="text-sm text-destructive/90 dark:text-red-200/90 mt-0.5 leading-relaxed">
                Your previous partner declined. Pick a new partner before
                entries close or cancel the entry to recover any refundable
                portion. The transaction fee stays in place either way.
              </p>
            </div>
          </div>

          <ul className="space-y-2 pl-1" role="list">
            {visible.map((row) => {
              const isBusy = cancellingId === row.registration_id;
              return (
                <li
                  key={row.registration_id}
                  className="rounded-md border border-destructive/30 dark:border-red-900/40 bg-background/80 dark:bg-red-950/20 px-3 py-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm truncate">
                        {row.event_name}
                      </p>
                      {row.position && (
                        <span className="text-xs text-muted-foreground">
                          {row.position}
                        </span>
                      )}
                    </div>
                    {row.rodeo_name && (
                      <p className="text-xs text-muted-foreground truncate">
                        {row.rodeo_name}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 sm:shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive border-destructive/40"
                      disabled={isBusy}
                      onClick={() =>
                        setPendingCancel({
                          id: row.registration_id,
                          description: row.event_name,
                        })
                      }
                      aria-label={`Cancel entry for ${row.event_name}`}
                    >
                      Cancel entry
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={isBusy}
                      onClick={() =>
                        setFindPartner({
                          registrationId: row.registration_id,
                          eventId: row.event_id,
                          primaryPosition: row.position,
                          eventName: row.event_name,
                        })
                      }
                      aria-label={`Find new partner for ${row.event_name}`}
                    >
                      Find Partner
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>

          {overflow > 0 && (
            <p className="text-xs text-muted-foreground pl-1">
              And {overflow} more — scroll the upcoming rodeos list below to
              act on the rest.
            </p>
          )}
        </CardContent>
      </Card>

      <FindNewPartnerDialog
        open={findPartner !== null}
        onClose={() => setFindPartner(null)}
        registrationId={findPartner?.registrationId ?? ''}
        eventId={findPartner?.eventId ?? null}
        primaryPosition={findPartner?.primaryPosition ?? null}
        eventName={findPartner?.eventName}
      />

      <CancelRegistrationDialog
        open={pendingCancel !== null}
        onClose={() => setPendingCancel(null)}
        onConfirm={handleCancelConfirm}
        description={pendingCancel?.description}
        loading={cancellingId !== null}
      />
    </>
  );
}
